import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas a la profundidad real de la carpeta Postventa
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 
import { deletePostVentaFull } from '../../../../firebase/firebaseUtils.js';

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * usePostventaTable - Hook de negocio encargado del estado del Tablero de PostVenta.
 */
export const usePostventaTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [polizas, setPolizas] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [activeItem, setActiveItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // REFS DE CONTROL DE CONCURRENCIA
    const activeItemPathRef = useRef(null);
    const isInitialHydrationDone = useRef(false);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache } = useDataHydration(polizas);

    // --- 2. LISTENER DE FIRESTORE EN TIEMPO REAL (Filtro postventa == Si) ---
    useEffect(() => {
        const q = query(
            collectionGroup(db, 'polizas'), 
            where('postventa', '==', 'Si')
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const rawData = snap.docs.map(d => ({ 
                id: d.id, 
                path: d.ref.path,
                clienteId: d.ref.path.split('/')[3],
                ...d.data() 
            }));

            setPolizas(rawData);
            setLoading(false);
        }, (error) => console.error("Error cargando polizas postventa:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return polizas.map(p => {
            const c = clientsCache[p.clienteId] || {};
            let nombreU = `${p.clienteNombre || ''} ${p.clienteApellido || ''}`.trim();
            if (!nombreU) {
                nombreU = c.persona === 'JURIDICA' ? (c.nombre || '') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
            }
            const displayDoc = p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';

            return { 
                ...p, 
                displayNombre: (nombreU || 'S/D').toUpperCase(), 
                displayDoc,
                displayCanalNombre: p.canalNombre || p.subcanal || '-'
            };
        });
    }, [polizas, clientsCache]);

    // Filtramos en caliente para excluir del tablero los casos marcados como "Cerrado"
    const activeItems = useMemo(() => {
        return itemsTable.filter(item => (item.postventa_estado || '').toLowerCase() !== 'cerrado');
    }, [itemsTable]);

    // --- 4. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN (useTableState) ---
    // Sincroniza de manera transparente filtros de multi-selección, buscadores y orden
    const tableState = useTableState({
        data: activeItems,
        initialSort: { key: 'postventa_fechaProximaGestion', direction: 'ascending' },
        initialColumns: {
            fechaCarga: true, displayNombre: true, displayDoc: true, nPoliza: true, compania: true,
            riesgo: true, postventa_numero_gestion: true, postventa_estado: true, postventa_quienGestiono: true,
            postventa_ultimoTipoGestion: true, postventa_fechaProximaGestion: true, canal: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear caso postventa:", e); }
    }, []);

    // --- 5. INTERCEPTOR DE CONTROLADORES DE ACCIÓN ---
    const handleAction = useCallback(async (item, type) => {
        setActiveItem(item);
        if (type === 'ficha') { 
            setShowFichaModal(true); 
            return; 
        }
        if (type === 'poliza') { 
            setShowPolicyModal(true); 
            return; 
        }
        if (type === 'delete') { 
            setShowDeleteConfirm(true); 
            return; 
        }

        if (type === 'edit') {
            activeItemPathRef.current = item.path;
            setShowEditModal(true);
            
            await updateDoc(doc(db, item.path), { 
                bloqueo: { 
                    userId: user?.uid || 'sys', 
                    userName: profile?.userName || 'Usuario',
                    timestamp: serverTimestamp() 
                } 
            }).catch(e => {
                console.error("Error al bloquear:", e);
                activeItemPathRef.current = null;
            });
        }
    }, [user, profile]);

    const confirmDelete = async () => {
        if (!activeItem) return;
        try {
            await deletePostVentaFull(activeItem.clienteId, activeItem.id);
            setShowDeleteConfirm(false);
            setActiveItem(null);
        } catch (e) { console.error("Error al eliminar postventa de base de datos:", e); }
    };

    return {
        loading,
        clientsCache,
        activeItem,
        showEditModal,
        showPolicyModal,
        showFichaModal,
        showDeleteConfirm,
        tableState,
        itemsTable,
        activeItems,
        activeItemPathRef,
        setShowEditModal,
        setShowPolicyModal,
        setShowFichaModal,
        setShowDeleteConfirm,
        setActiveItem,
        handleAction,
        confirmDelete,
        unlockPoliza
    };
};